import { Loader, Tooltip } from 'components';
import PopupWrapper from 'components/Popups/Wrapper';
import InvoicePayerEmailsField, {
  type InvoicePayerEmailsFieldHandle,
} from 'pages/Subscriptions/SubscriptionInner/InvoicePayerEmailsField';
import {
  isYupValidationError,
  validateInvoicePayerEmails,
} from 'pages/Subscriptions/SubscriptionInner/invoicePayerEmailsSchema';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { updateSubscriptionPayerEmailsRequest } from 'services/subscriptions';
import { CloseIconBigger, EditIcon } from 'static';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { toast as addAppToast } from 'utils/toastAbstraction';

import { BillToEdit } from './styled';
import {
  CloseButton,
  ContentArea,
  LoaderSlot,
  LoadingOverlay,
  LoadingText,
  ModalActions,
  ModalBtnPrimary,
  ModalBtnSecondary,
  ModalError,
  ModalFooter,
  ModalHeader,
  ModalPanel,
  ModalSubtitle,
  ModalTitle,
} from './SwitchBillingModeModal.styled';

const panelMotion = {
  initial: { opacity: 0, scale: 0.96, y: 14 },
  animate: { opacity: 1, scale: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
};

const sameEmails = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  return left.every(
    (email, index) => email.toLowerCase() === right[index]?.toLowerCase(),
  );
};

type EditPayerEmailsControlProps = {
  subscriptionId: string;
  payerEmails: string[];
  defaultEmail?: string;
};

const EditPayerEmailsControl = ({
  subscriptionId,
  payerEmails,
  defaultEmail,
}: EditPayerEmailsControlProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const emailFieldRef = useRef<InvoicePayerEmailsFieldHandle>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [emails, setEmails] = useState<string[]>(payerEmails);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openModal = () => {
    setEmails(payerEmails);
    setError(null);
    setSubmitting(false);
    setIsOpen(true);
  };

  const closeModal = useCallback(() => {
    if (submitting) return;
    setIsOpen(false);
  }, [submitting]);

  const handleSave = useCallback(async () => {
    if (submitting) return;
    const flushed = emailFieldRef.current?.flush();
    if (!flushed || flushed.error) return;

    let nextEmails: string[];
    try {
      nextEmails = validateInvoicePayerEmails(flushed.emails, t);
    } catch (err) {
      if (isYupValidationError(err)) {
        setError(err.errors[0] ?? err.message);
      }
      return;
    }

    if (sameEmails(nextEmails, payerEmails)) {
      setIsOpen(false);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await updateSubscriptionPayerEmailsRequest({
        subscriptionId,
        payerEmails: nextEmails,
      });
      setIsOpen(false);
      addAppToast(
        { title: t('notifications.payer_emails_updated') },
        {
          appearance: 'success',
          autoDismiss: true,
        },
      );
      dispatch(getCurrentUserSubscription.trigger());
    } catch {
      setError(t('subscriptions.profile.payment.editRecipientsError'));
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, payerEmails, submitting, subscriptionId, t]);

  return (
    <>
      <Tooltip
        text={t('subscriptions.profile.payment.editRecipients')}
        position="top-center"
      >
        <BillToEdit
          type="button"
          onClick={openModal}
          aria-label={t('subscriptions.profile.payment.editRecipients')}
        >
          <EditIcon width={12} height={12} aria-hidden />
        </BillToEdit>
      </Tooltip>
      <PopupWrapper isOpen={isOpen}>
        <ModalPanel
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-payer-emails-title"
          {...panelMotion}
        >
          <ContentArea>
            <ModalHeader>
              <ModalTitle id="edit-payer-emails-title">
                {t('subscriptions.profile.payment.editRecipientsTitle')}
              </ModalTitle>
              <ModalSubtitle>
                {t('subscriptions.profile.payment.editRecipientsSubtitle')}
              </ModalSubtitle>
            </ModalHeader>

            <InvoicePayerEmailsField
              ref={emailFieldRef}
              emails={emails}
              onChange={setEmails}
              inputId="edit-payer-emails"
              label={t('subscriptions.checkout.chf.billingEmail')}
              placeholder={t('subscriptions.checkout.invoiceEmailPlaceholder')}
              optional
            />

            <ModalFooter>
              {error ? <ModalError role="alert">{error}</ModalError> : null}
              <ModalActions>
                <ModalBtnSecondary
                  type="button"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </ModalBtnSecondary>
                <ModalBtnPrimary
                  type="button"
                  onClick={handleSave}
                  disabled={submitting}
                >
                  {submitting
                    ? t('subscriptions.profile.payment.editRecipientsSaving')
                    : t('subscriptions.profile.payment.editRecipientsSave')}
                </ModalBtnPrimary>
              </ModalActions>
            </ModalFooter>
          </ContentArea>

          <CloseButton
            type="button"
            onClick={closeModal}
            disabled={submitting}
            aria-label={t('common.cancel')}
          >
            <CloseIconBigger />
          </CloseButton>

          {submitting ? (
            <LoadingOverlay aria-live="polite" aria-busy="true">
              <LoaderSlot>
                <Loader size="56px" type={null} />
              </LoaderSlot>
              <LoadingText>
                {t('subscriptions.profile.payment.editRecipientsSaving')}
              </LoadingText>
            </LoadingOverlay>
          ) : null}
        </ModalPanel>
      </PopupWrapper>
    </>
  );
};

export default EditPayerEmailsControl;
